import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './useAppHooks';
import { logout } from '../features/auth/authSlice';

// User is logged out after this many ms of no input. Matches the backend's
// INACTIVITY_TIMEOUT_MINUTES (default 60 min) so both sides agree. The
// backend check only fires on an API call, so without this hook a user who
// walks away from the browser stays "logged in" indefinitely from their
// browser's perspective until they click something.
const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000;   // 60 minutes
// Show a warning toast this long before logout
const WARN_BEFORE_MS = 2 * 60 * 1000;        // 2 minutes

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click',
];

export const useInactivityTimer = (timeoutMs: number = DEFAULT_TIMEOUT_MS) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const resetTimer = () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
      warnedRef.current = false;

      warnTimer.current = setTimeout(() => {
        warnedRef.current = true;
        // Soft notice — non-blocking. We deliberately don't use a modal here
        // because that would itself count as "activity" if the user dismisses
        // it, defeating the purpose. A console + window event lets any UI
        // chrome (e.g. a snackbar) surface it.
        console.warn('Session will expire in 2 minutes due to inactivity');
        window.dispatchEvent(new CustomEvent('rfm:inactivity-warning'));
      }, Math.max(0, timeoutMs - WARN_BEFORE_MS));

      logoutTimer.current = setTimeout(() => {
        console.warn('Session expired due to inactivity — logging out');
        window.dispatchEvent(new CustomEvent('rfm:inactivity-logout'));
        dispatch(logout());
      }, timeoutMs);
    };

    // Start the timer immediately on mount
    resetTimer();

    // Listen for user activity. passive:true so we don't block scroll.
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));

    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [isAuthenticated, dispatch, timeoutMs]);
};

export default useInactivityTimer;
